// Zero-width characters: a delimiter around bits encoded as ZWNJ (0) and ZWJ (1).
const DELIMITER = '\u2060';
const ZERO = '\u200C';
const ONE = '\u200D';
const BITS_PER_CHAR = 8;
const MARKER_PATTERN = new RegExp(`${DELIMITER}([${ZERO}${ONE}]+)${DELIMITER}`, 'g');

/**
 * Encodes an i18n key as an invisible marker.
 * @param {string} key - the i18n key, made of single-byte characters.
 * @returns {string} the zero-width marker.
 */
const encodeKeyMarker = (key) => {
  const bits = Array.from(key).map((char) => {
    const code = char.charCodeAt(0);
    if (code > 0xff) {
      throw new Error(`i18n capture: key "${key}" contains an unsupported character`);
    }
    return code.toString(2).padStart(BITS_PER_CHAR, '0');
  }).join('');

  return `${DELIMITER}${bits.replace(/0/g, ZERO).replace(/1/g, ONE)}${DELIMITER}`;
};

/**
 * Decodes every key marker found in a string.
 * @param {string} text - text that may contain markers.
 * @returns {Array<{key: string, offset: number}>} the keys in order, with the marker offset.
 */
const decodeKeyMarkers = (text) => {
  if (typeof text !== 'string' || text.indexOf(DELIMITER) === -1) {
    return [];
  }

  return Array.from(text.matchAll(MARKER_PATTERN))
    .filter(([, encoded]) => encoded.length % BITS_PER_CHAR === 0)
    .map(({ 1: encoded, index }) => {
      const bits = encoded.replaceAll(ZERO, '0').replaceAll(ONE, '1');
      const chars = bits.match(new RegExp(`.{${BITS_PER_CHAR}}`, 'g'))
        .map((byte) => String.fromCharCode(parseInt(byte, 2)));
      return { key: chars.join(''), offset: index };
    });
};

// Shared by webpack's capture build and the Node/Playwright journey runner.
module.exports = { encodeKeyMarker, decodeKeyMarkers };
